import { normalize_query, user_matches } from '../../../util_components/user_combobox/user_match';
import type { RoleUser } from './api';
import type { TeamMember } from '../../sales_team/api';
import { sort_roster } from '../../sales_team/roster';

export const SELF_REMOVE_HINT = 'You cannot remove yourself. Ask another admin.';

export const display_name = (u: RoleUser): string => u.name || u.email || u.uuid;

export const candidate_label = (u: RoleUser): string => {
  const name = display_name(u);
  const email = u.email && u.email !== name ? ` (${u.email})` : '';
  return `${name}${email}${u.is_active ? '' : ' — inactive'}`;
};

const by_name = (a: RoleUser, b: RoleUser): number =>
  display_name(a).localeCompare(display_name(b), undefined, { sensitivity: 'base' }) ||
  a.uuid.localeCompare(b.uuid);

export const sort_members = (members: RoleUser[] | null | undefined): RoleUser[] =>
  [...(members || [])].sort(by_name);

// Existing members are excluded even if the server lists them as candidates.
export const filter_candidates = (
  candidates: RoleUser[] | null | undefined,
  query: string,
  members: RoleUser[] | null | undefined = []
): RoleUser[] => {
  const taken = new Set((members || []).map((m) => m.uuid));
  const q = normalize_query(query);
  return sort_members(candidates).filter((c) => !taken.has(c.uuid) && user_matches(c, q));
};

export interface RemoveCheck {
  ok: boolean;
  hint?: string;
}

export const can_remove = (member: RoleUser, self_uuid: string | null | undefined): RemoveCheck =>
  self_uuid && member.uuid === self_uuid ? { ok: false, hint: SELF_REMOVE_HINT } : { ok: true };

export interface PanelRow {
  uuid: string;
  name: string;
  email: string;
  is_active: boolean;
  // null when the user holds the SALES role but the roster does not list them.
  member: TeamMember | null;
}

export const row_user = (row: PanelRow): RoleUser => ({
  uuid: row.uuid,
  name: row.name,
  email: row.email,
  is_active: row.is_active
});

// Roster order first, then role members the roster omits (e.g. inactive users).
export const merge_rows = (
  roster: TeamMember[] | null | undefined,
  role_members: RoleUser[] | null | undefined
): PanelRow[] => {
  const by_uuid = new Map((role_members || []).map((m) => [m.uuid, m]));
  const listed = new Set<string>();
  const rows: PanelRow[] = sort_roster(roster).map((m) => {
    listed.add(m.ops_uuid);
    return {
      uuid: m.ops_uuid,
      name: m.name || '',
      email: m.email || '',
      is_active: by_uuid.get(m.ops_uuid)?.is_active ?? true,
      member: m
    };
  });
  sort_members(role_members)
    .filter((u) => !listed.has(u.uuid))
    .forEach((u) => rows.push({ ...u, member: null }));
  return rows;
};
